import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';

export interface MEOption {
  ME_KEY: number;
  ME_CODE: string;
  ME_NAME?: string;
  [key: string]: unknown;
}

export interface FSAccountOption {
  ACCT: string;
  ACCT_DESC?: string;
  [key: string]: unknown;
}

export interface VarianceTypeOption {
  VARIANCE_TYPE: string;
  [key: string]: unknown;
}

export interface EntryDataItem {
  AMOUNT_1204?: number;
  AMOUNT_1205?: number;
  AMOUNT_1206?: number;
  AMOUNT_1207?: number;
  COMMENTS?: string;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class CbcReconciliationService {
  constructor(private http: HttpClient) {}

  private getParams(
    globalService: any,
    filterParams: any,
    ssoId: string,
    extra: Record<string, string | number | null | undefined> = {}
  ): HttpParams {
    const g = globalService || {};
    const f = filterParams || {};
    let p = new HttpParams();
    if (ssoId) p = p.set('sso_id', ssoId);
    const taxYear = f.tax_year != null ? f.tax_year : g.tax_year;
    if (taxYear != null) p = p.set('tax_year', String(taxYear));
    const scenario = f.scenario != null ? f.scenario : g.scenario;
    if (scenario != null) p = p.set('scenario', String(scenario));
    const jcdKey = f.jcd_key != null ? f.jcd_key : g.jcd_key;
    if (jcdKey != null) p = p.set('jcd_key', String(jcdKey));
    const scenarioCode = (f.scenarioCode ?? f.scenario_code) != null ? (f.scenarioCode ?? f.scenario_code) : (g.scenarioCode ?? g.scenario_code);
    if (scenarioCode != null) p = p.set('scenarioCode', String(scenarioCode));
    const issueKeyEnabled = f.is_issue_key_enabled != null ? f.is_issue_key_enabled : g.is_issue_key_enabled;
    if (issueKeyEnabled != null) p = p.set('is_issue_key_enabled', String(issueKeyEnabled));
    Object.keys(extra).forEach((k) => {
      const v = extra[k];
      if (v !== undefined && v !== null) p = p.set(k, String(v));
    });
    return p;
  }

  getMEData(baseUrl: string, filterParams: any, ssoId: string, globalService: any): Observable<MEOption[]> {
    const url = `${baseUrl}/loadJsonObject?action_code=vfv6h5`;
    const params = this.getParams(globalService, filterParams, ssoId);
    return this.http.get<{ callSuccess: string; jsonObject?: MEOption[]; errorMessage?: string }>(url, { params }).pipe(
      map((res) => {
        if (res.callSuccess === '1' && res.jsonObject) return res.jsonObject;
        throw new Error(res.errorMessage || 'Failed to load ME data');
      })
    );
  }

  getFSAccounts(baseUrl: string, filterParams: any, ssoId: string, globalService: any): Observable<FSAccountOption[]> {
    const url = `${baseUrl}/loadJsonObject?action_code=9jwcbi`;
    const params = this.getParams(globalService, filterParams, ssoId);
    return this.http.get<{ callSuccess: string; jsonObject?: FSAccountOption[]; errorMessage?: string }>(url, { params }).pipe(
      map((res) => {
        if (res.callSuccess === '1' && res.jsonObject) return res.jsonObject;
        throw new Error(res.errorMessage || 'Failed to load FS accounts');
      })
    );
  }

  getEntryData(
    baseUrl: string,
    filterParams: any,
    ssoId: string,
    meCode: string,
    acct: string,
    varType: string | null,
    globalService: any = {}
  ): Observable<EntryDataItem[]> {
    const url = `${baseUrl}/loadJsonObject?action_code=7fyyl9`;
    const params = this.getParams(globalService, filterParams, ssoId, {
      me_code: meCode,
      acct,
      var_type: varType ?? undefined,
    });
    return this.http.post<{ callSuccess: string; jsonObject?: EntryDataItem[]; errorMessage?: string }>(url, null, { params }).pipe(
      map((res) => {
        if (res.callSuccess === '1' && res.jsonObject) return res.jsonObject;
        throw new Error(res.errorMessage || 'Failed to load entry data');
      })
    );
  }

  saveData(
    baseUrl: string,
    clobData: any[],
    filterParams: any,
    ssoId: string,
    globalService: any
  ): Observable<{ callSuccess: string; errorMessage?: string }> {
    const url = `${baseUrl}/saveJsonObject?action_code=5rex72`;
    const params = this.getParams(globalService, filterParams, ssoId);
    const body = params.set('clob_data', JSON.stringify(clobData)).toString();
    return this.http.post<{ callSuccess: string; errorMessage?: string }>(url, body, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  }

  reconcFSAccounts(baseUrl: string, filterParams: any, ssoId: string, globalService: any): Observable<FSAccountOption[]> {
    const url = `${baseUrl}/loadJsonObject?action_code=o492e4`;
    const params = this.getParams(globalService, filterParams, ssoId);
    return this.http.get<{ callSuccess: string; jsonObject?: FSAccountOption[]; errorMessage?: string }>(url, { params }).pipe(
      map((res) => {
        if (res.callSuccess === '1' && res.jsonObject) return res.jsonObject;
        throw new Error(res.errorMessage || 'Failed to load reconciling FS accounts');
      })
    );
  }

  getVarianceType(baseUrl: string, filterParams: any, ssoId: string, globalService: any = {}): Observable<VarianceTypeOption[]> {
    const url = `${baseUrl}/loadJsonObject?action_code=6abqlx`;
    const params = this.getParams(globalService, filterParams, ssoId);
    return this.http.get<{ callSuccess: string; jsonObject?: VarianceTypeOption[]; errorMessage?: string }>(url, { params }).pipe(
      map((res) => {
        if (res.callSuccess === '1' && res.jsonObject) return res.jsonObject;
        throw new Error(res.errorMessage || 'Failed to load variance types');
      })
    );
  }

  deleteUserVariance(
    baseUrl: string,
    varianceIds: string,
    filterParams: any,
    ssoId: string,
    globalService: any
  ): Observable<{ callSuccess: string; errorMessage?: string }> {
    const url = `${baseUrl}/saveJsonObject?action_code=1n9e3l`;
    const body = this.getParams(globalService, filterParams, ssoId, { variance_key: varianceIds }).toString();
    return this.http.post<{ callSuccess: string; errorMessage?: string }>(url, body, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  }
}
